import { useEffect } from "react";
import settings from "@content/settings.json";

/**
 * Conditional analytics/tracking loader.
 * Reads IDs from content/settings.json. If any ID is empty (default),
 * that tracker is NOT loaded — the site ships zero third-party scripts.
 *
 * Supported:
 *   - Google Analytics 4    → settings.googleAnalytics   (e.g. G-XXXXXX)
 *   - Google Tag Manager    → settings.googleTagManager  (e.g. GTM-XXXXXX)
 *   - Meta (Facebook) Pixel → settings.metaPixel         (e.g. 1234567890)
 *   - Microsoft Clarity     → settings.microsoftClarity  (e.g. abc123xyz)
 */
const injectScript = (id, src, inline = null, async = true) => {
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    if (src) s.src = src;
    if (async) s.async = true;
    if (inline) s.text = inline;
    document.head.appendChild(s);
};

const Tracking = () => {
    useEffect(() => {
        const {
            googleAnalytics: ga,
            googleTagManager: gtm,
            metaPixel: pixel,
            microsoftClarity: clarity,
        } = settings;

        // Google Analytics 4
        if (ga && /^G-/.test(ga)) {
            injectScript(
                "ga4-loader",
                `https://www.googletagmanager.com/gtag/js?id=${ga}`
            );
            injectScript(
                "ga4-init",
                null,
                `window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${ga}', { anonymize_ip: true });`,
                false
            );
        }

        // Google Tag Manager
        if (gtm && /^GTM-/.test(gtm)) {
            injectScript(
                "gtm-init",
                null,
                `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                })(window,document,'script','dataLayer','${gtm}');`,
                false
            );
        }

        // Meta (Facebook) Pixel
        if (pixel && /^\d+$/.test(pixel)) {
            injectScript(
                "fbq-init",
                null,
                `!function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${pixel}');
                fbq('track', 'PageView');`,
                false
            );
        }

        // Microsoft Clarity
        if (clarity && /^[a-z0-9]+$/i.test(clarity)) {
            injectScript(
                "clarity-init",
                null,
                `(function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                })(window, document, "clarity", "script", "${clarity}");`,
                false
            );
        }
    }, []);

    return null;
};

export default Tracking;
